import {
  decodeIdeaSubscriptionList,
  decodeSubscriptionCancelResponse,
  decodeTradeIdeaDetail,
  decodeTradeReferenceMintResponse,
  decodeTradeIdeasList,
  decodeMultiStrategyScreen,
  decodeEditionDetail,
  decodeEditionArchiveList,
  decodeLedgerRows,
  decodeLedgerPerformance,
  decodeEditionIntegrityTrail,
  type MultiStrategyScreenList,
  type IdeaSubscriptionList,
  type SubscriptionCancelResponse,
  type TradeIdeaDetail,
  type TradeReferenceMintResponse,
  type TradeIdeasList,
  type EditionDetail,
  type EditionArchiveList,
  type LedgerRowsResponse,
  type PerformanceAggregatesResponse,
  type EditionIntegrityTrail,
} from "@/domains/trade-ideas/lib/contracts";
import { queryKeys } from "@/shared/lib/queryKeys";
import type { ApiFn } from "@/shared/lib/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface IdeaFilters {
  readiness?: "CRITERIA_MET" | "CONDITIONS_PENDING";
  strategy_family?: "long_call" | "long_put" | "bull_call_debit" | "bear_put_debit";
}

// JS-1098 (Codex round, MEDIUM): `useTradeIdeaSetups`/`useTradeIdeaSetupUniverse`/
// `useTradeIdeaSetup` (setup-first reads) and `useMintSetupReference`/
// `usePreviewSetupSubscription`/`useConfirmSetupSubscription`/
// `useTradeIdeaSetupSubscriptions`/`useCancelSetupSubscription` (setup
// reference/subscription writes) were removed from this hooks layer --
// reviewer-confirmed zero callers anywhere in the frontend once the legacy
// setups-list page rendering (TradeIdeasPage's SetupDecisionQueue/
// SetupDetailDialog/SetupUniverseFunnel, all deleted) was retired. The
// Desk's own dependency on the setups pipeline is server-side only (the
// screener/edition endpoints already read setup_binding server-side; no
// frontend surface calls the standalone `/setups` endpoints any more).
// The underlying setup_contracts decoders/types in contracts.ts are
// UNTOUCHED (still exercised directly by setupContracts.test.ts) -- only
// this now-dead frontend query-hook layer over them was deleted.

export function useMintTradeReference(api: ApiFn) {
  return useMutation<TradeReferenceMintResponse, Error, { opportunitySnapshotId: string; candidateId?: string }>({
    mutationFn: async ({ opportunitySnapshotId, candidateId }) => decodeTradeReferenceMintResponse(
      await api("POST", "/api/v1/trade-ideas/references", {
        opportunity_snapshot_id: opportunitySnapshotId,
        referenced_candidate_id: candidateId,
      }, { silent: true }),
    ),
  });
}

function listPath(filters: IdeaFilters) {
  const params = new URLSearchParams({ limit: "25" });
  if (filters.readiness) params.set("readiness", filters.readiness);
  if (filters.strategy_family) params.set("strategy_family", filters.strategy_family);
  return `/api/v1/trade-ideas?${params.toString()}`;
}

export function useTradeIdeas(api: ApiFn, filters: IdeaFilters) {
  return useQuery<TradeIdeasList>({
    queryKey: queryKeys.tradeIdeas({
      readiness: filters.readiness,
      strategy_family: filters.strategy_family,
    }),
    queryFn: async () => decodeTradeIdeasList(await api("GET", listPath(filters))),
    staleTime: 30_000,
    retry: false,
  });
}

export function useTradeIdea(api: ApiFn, candidateId: string | null) {
  return useQuery<TradeIdeaDetail>({
    queryKey: queryKeys.tradeIdea(candidateId ?? "none"),
    queryFn: async () => decodeTradeIdeaDetail(await api("GET", `/api/v1/trade-ideas/${encodeURIComponent(candidateId!)}`)),
    enabled: Boolean(candidateId),
    staleTime: 30_000,
    retry: false,
  });
}

export function useTradeIdeaSubscriptions(api: ApiFn) {
  return useQuery<IdeaSubscriptionList>({
    queryKey: queryKeys.tradeIdeaSubscriptions(),
    queryFn: async () => decodeIdeaSubscriptionList(
      await api("GET", "/api/v1/trade-ideas/subscriptions?limit=100"),
    ),
    staleTime: 15_000,
    retry: false,
  });
}

export function useCancelTradeIdeaSubscription(api: ApiFn) {
  const queryClient = useQueryClient();
  return useMutation<SubscriptionCancelResponse, Error, string>({
    mutationFn: async (subscriptionId) => decodeSubscriptionCancelResponse(
      await api(
        "DELETE",
        `/api/v1/trade-ideas/subscriptions/${encodeURIComponent(subscriptionId)}`,
        undefined,
        { silent: true },
      ),
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.tradeIdeaSubscriptions() });
    },
  });
}

// Barebones read-only hook for the INTERNAL_SHADOW_RESEARCH multi-strategy
// screener. Mirrors the setup/opportunity read hooks above.
//
// ``curated`` (default true) asks the API for only the ideas that cleared the
// best-ideas quality bar (best-first); ``curated=false`` requests the full
// score-sorted list ("Show all"). The flag is part of the query key so the
// two views cache independently and the toggle triggers a refetch.
export function useMultiStrategyScreen(api: ApiFn, enabled = true, curated = true) {
  return useQuery<MultiStrategyScreenList>({
    queryKey: [...queryKeys.tradeIdeasRoot(), "screener", "list", { curated }],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: "100", curated: String(curated) });
      return decodeMultiStrategyScreen(
        await api("GET", `/api/v1/trade-ideas/screener?${params.toString()}`),
      );
    },
    staleTime: 30_000,
    retry: false,
    enabled,
  });
}

// JS-1060: the immutable daily edition read API. Internal-only (allowlist-gated
// via the SAME authorization boundary as the screener above -- a 403 means the
// caller isn't internal; see EditionPage's handling of ApiError.status).
//
// "Today" means "the most recently generated edition" (see
// EditionReadService.get_today) -- moderate staleTime so a page left open
// through the ~16:05 ET generation window eventually picks up the new
// edition without needing a hard refresh.
//
// FOLLOW-UP 7: the app disables refetchOnWindowFocus globally, so staleTime
// alone would never actually trigger that refetch on an idle-but-open tab --
// a reader who opens the page before 16:05 ET and never refocuses the
// window would sit on the stale "yesterday" edition indefinitely. A 5-minute
// polling interval (well above the 60s staleTime, cheap for a single small
// read) closes that gap. Only THIS hook polls -- the dated/archive hooks
// stay on their existing aggressive-cache-forever posture (published
// editions never change, so there is nothing new to poll for).
export function useTradeIdeaEditionToday(api: ApiFn, enabled = true) {
  return useQuery<EditionDetail>({
    queryKey: queryKeys.tradeIdeaEditionToday(),
    queryFn: async () => decodeEditionDetail(await api("GET", "/api/v1/trade-ideas/editions/today")),
    staleTime: 60_000,
    refetchInterval: 300_000,
    retry: false,
    enabled,
  });
}

// An edition is write-once/immutable once generated (see edition.py's
// "Write-once semantics") -- a SPECIFIC as_of's content can never change once
// it exists, so this caches aggressively (Infinity: never refetch on its own;
// only a fresh mount/query-key change re-fetches).
export function useTradeIdeaEditionByAsOf(api: ApiFn, asOf: string | null, enabled = true) {
  return useQuery<EditionDetail>({
    queryKey: queryKeys.tradeIdeaEditionByAsOf(asOf ?? "none"),
    queryFn: async () => decodeEditionDetail(
      await api("GET", `/api/v1/trade-ideas/editions/${encodeURIComponent(asOf!)}`),
    ),
    enabled: enabled && Boolean(asOf),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
}

export interface EditionArchiveFilters {
  limit?: number;
  beforeAsOf?: string;
}

// The archive listing page is a paginated cursor read (before_as_of) -- each
// distinct cursor is its own cache entry, same aggressive caching rationale
// as the by-as-of detail read above (published editions never change).
export function useTradeIdeaEditionArchive(api: ApiFn, filters: EditionArchiveFilters = {}, enabled = true) {
  const limit = filters.limit ?? 25;
  return useQuery<EditionArchiveList>({
    queryKey: queryKeys.tradeIdeaEditionArchive({ limit, beforeAsOf: filters.beforeAsOf }),
    queryFn: async () => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (filters.beforeAsOf) params.set("before_as_of", filters.beforeAsOf);
      return decodeEditionArchiveList(
        await api("GET", `/api/v1/trade-ideas/editions?${params.toString()}`),
      );
    },
    staleTime: 60_000,
    retry: false,
    enabled,
  });
}

// JS-1090b: audit-grade outcome-ledger rows + performance aggregates. Same
// internal-allowlist authorization boundary as the screener/edition (a 403
// means "not an internal user" -- see LedgerPage/PerformancePage's handling
// of ApiError.status, identical to EditionPage's pattern).

export interface LedgerRowFiltersInput {
  status?: "OPEN" | "RESOLVED" | "FLAGGED";
  archetype?: string;
  direction?: string;
  lane?: string;
  dateFrom?: string;
  dateTo?: string;
  // Round 2 (adversarial review, MED FIX 5 -- ledger targeting): an exact
  // ledger_id equality filter (additive backend param -- see
  // outcome_ledger_reads._shared_match's docstring). Used by LedgerPage's
  // "target one exact row" flow (drawdown peak/trough links, the ticker
  // "back to idea" link) -- NEVER derived from the row_id reconciliation
  // contract (see tickerContextLink.ts's LEDGER_TARGET_PARAM doc comment).
  ledgerId?: string;
  limit?: number;
  cursor?: string;
}

function ledgerRowsPath(filters: LedgerRowFiltersInput) {
  const params = new URLSearchParams({ limit: String(filters.limit ?? 50) });
  if (filters.status) params.set("status", filters.status);
  if (filters.archetype) params.set("archetype", filters.archetype);
  if (filters.direction) params.set("direction", filters.direction);
  if (filters.lane) params.set("lane", filters.lane);
  if (filters.dateFrom) params.set("date_from", filters.dateFrom);
  if (filters.dateTo) params.set("date_to", filters.dateTo);
  if (filters.ledgerId) params.set("ledger_id", filters.ledgerId);
  if (filters.cursor) params.set("cursor", filters.cursor);
  return `/api/v1/trade-ideas/outcome-ledger/rows?${params.toString()}`;
}

// Cursor pagination: each distinct filter+cursor combination is its own cache
// entry (mirrors useTradeIdeaEditionArchive's beforeAsOf-keyed caching above).
// A signed cursor expires after 15 minutes server-side (CURSOR_TTL_SECONDS),
// so this does not cache aggressively -- a moderate staleTime keeps a
// same-session back/forward click snappy without risking a stale cursor being
// replayed well past its window.
export function useTradeIdeaLedgerRows(api: ApiFn, filters: LedgerRowFiltersInput, enabled = true) {
  return useQuery<LedgerRowsResponse>({
    queryKey: queryKeys.tradeIdeaLedgerRows({
      status: filters.status,
      archetype: filters.archetype,
      direction: filters.direction,
      lane: filters.lane,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      ledgerId: filters.ledgerId,
      limit: filters.limit,
      cursor: filters.cursor,
    }),
    queryFn: async () => decodeLedgerRows(await api("GET", ledgerRowsPath(filters))),
    staleTime: 30_000,
    retry: false,
    enabled,
  });
}

export function useTradeIdeaLedgerPerformance(api: ApiFn, enabled = true) {
  return useQuery<PerformanceAggregatesResponse>({
    queryKey: queryKeys.tradeIdeaLedgerPerformance(),
    queryFn: async () => decodeLedgerPerformance(
      await api("GET", "/api/v1/trade-ideas/outcome-ledger/performance"),
    ),
    staleTime: 30_000,
    retry: false,
    enabled,
  });
}

// On-demand "Verify integrity" click -- a mutation wrapping a GET-like action
// (mirrors useMintSetupReference's shape above), keyed by the `as_of` passed
// at call time rather than an auto-fetched query, since this is a
// reader-triggered check, not page-load data.
export function useEditionIntegrityCheck(api: ApiFn) {
  return useMutation<EditionIntegrityTrail, Error, string>({
    mutationFn: async (asOf) => decodeEditionIntegrityTrail(
      await api(
        "GET",
        `/api/v1/trade-ideas/editions/${encodeURIComponent(asOf)}/integrity`,
        undefined,
        { silent: true },
      ),
    ),
  });
}
